import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Book,
  Home,
  Rocket,
  Phone,
  MessageSquare,
  Facebook,
  Globe,
  Users,
  Workflow,
  UserCog,
  Mail,
  GitBranch,
  Shield,
  Search,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Filter,
  FormInput,
  User,
  RefreshCw
} from 'lucide-react';

interface DocSection {
  title: string;
  items: {
    name: string;
    path: string;
    icon: React.ReactNode;
  }[];
}

const DocumentationLayout: React.FC = () => {
  const location = useLocation();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Getting Started', 'Integrations', 'Features'])
  );

  // Scroll to top when location changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const docSections: DocSection[] = [
    {
      title: 'Getting Started',
      items: [
        { name: 'Overview', path: '/docs', icon: <Home className="w-4 h-4" /> },
        { name: 'Quick Start Guide', path: '/docs/getting-started', icon: <Rocket className="w-4 h-4" /> },
        { name: 'User Profile', path: '/docs/user-profile', icon: <User className="w-4 h-4" /> },
        { name: 'Account Switching', path: '/docs/account-switching', icon: <RefreshCw className="w-4 h-4" /> },
        { name: 'Organization Information', path: '/docs/client-information', icon: <Building2 className="w-4 h-4" /> }
      ]
    },
    {
      title: 'Integrations',
      items: [
        { name: 'Phone Integration', path: '/docs/integrations/phone', icon: <Phone className="w-4 h-4" /> },
        { name: 'WhatsApp Integration', path: '/docs/integrations/whatsapp', icon: <MessageSquare className="w-4 h-4" /> },
        { name: 'Facebook Pages', path: '/docs/integrations/facebook', icon: <Facebook className="w-4 h-4" /> },
        { name: 'Website Forms', path: '/docs/integrations/website', icon: <Globe className="w-4 h-4" /> }
      ]
    },
    {
      title: 'Features',
      items: [
        { name: 'Lead Management', path: '/docs/lead-management', icon: <Users className="w-4 h-4" /> },
        { name: 'Pipeline & Stages', path: '/docs/pipeline', icon: <Workflow className="w-4 h-4" /> },
        { name: 'Funnel Management', path: '/docs/funnel-management', icon: <Filter className="w-4 h-4" /> },
        { name: 'Form Fields', path: '/docs/form-fields', icon: <FormInput className="w-4 h-4" /> },
        { name: 'Round Robin Assignment', path: '/docs/round-robin', icon: <GitBranch className="w-4 h-4" /> },
        { name: 'Drip Campaigns', path: '/docs/drip-campaigns', icon: <Mail className="w-4 h-4" /> },
        { name: 'User Management', path: '/docs/user-management', icon: <UserCog className="w-4 h-4" /> },
        { name: 'Roles & Permissions', path: '/docs/roles-permissions', icon: <Shield className="w-4 h-4" /> }
      ]
    }
  ];

  const toggleSection = (title: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedSections(newExpanded);
  };

  const isActive = (path: string) => {
    if (path === '/docs') {
      return location.pathname === '/docs';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? 'w-72' : 'w-0'
        } bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-brandColor p-2 rounded-lg">
                <Book className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Documentation</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Complete guides & tutorials</p>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              {isSidebarOpen ? <X className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {docSections.map((section) => {
            const isExpanded = expandedSections.has(section.title);

            return (
              <div key={section.title} className="space-y-1">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <span>{section.title}</span>
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>

                {/* Section Items */}
                {isExpanded && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive(item.path)
                            ? 'bg-green-50 dark:bg-green-900/20 text-brandColor dark:text-green-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className={isActive(item.path) ? 'text-brandColor dark:text-green-400' : 'text-gray-400'}>
                          {item.icon}
                        </div>
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="mt-auto p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p className="mb-1">Need more help?</p>
            <a href="mailto:support@yourcrm.com" className="text-brandColor dark:text-green-400 hover:underline">
              Contact Support
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main ref={mainContentRef} className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default DocumentationLayout;
